(() => {
  'use strict';
  const config = window.WAF || {};
  const strings = config.strings || {};
  const root = config.root || '/';
  const path = name => `${root}${name}`;
  const initialized = new WeakSet();
  const infiniteStates = new Map();
  const openers = new WeakMap();
  const headerObservers = new Map();
  const mobileMedia = matchMedia('(max-width:749px)');
  let cartTask = Promise.resolve();
  function cartQueue(task) { const next = cartTask.then(task, task); cartTask = next.catch(() => {}); return next; }
  function setStatus(message) { const el = document.querySelector('[data-store-status]'); if (el) el.textContent = message; }
  function setMobileMenu(header, open, returnFocus = false) {
    if (!header) return;
    const toggle = header.querySelector('[data-mobile-menu-toggle]');
    const navigation = header.querySelector('.header-navigation');
    const expanded = mobileMedia.matches && open;
    header.classList.toggle('mobile-menu-open', expanded);
    if (toggle) {
      toggle.setAttribute('aria-expanded', String(expanded));
      toggle.textContent = expanded ? toggle.dataset.closeLabel : toggle.dataset.openLabel;
      if (returnFocus && mobileMedia.matches) toggle.focus({preventScroll:true});
    }
    if (navigation) navigation.inert = mobileMedia.matches && !expanded;
  }
  function initHeader(header) {
    if (headerObservers.has(header)) return;
    function measure() {
      const sidebar = header.dataset.sidebar === 'true' && !mobileMedia.matches;
      const height = sidebar ? 0 : Math.ceil(header.getBoundingClientRect().height);
      document.documentElement.style.setProperty('--header-height', `${height}px`);
      document.documentElement.style.setProperty('--sticky-offset', header.dataset.stickyHeader === 'true' ? `${height}px` : '0px');
    }
    const observer = 'ResizeObserver' in window ? new ResizeObserver(measure) : null;
    observer?.observe(header);
    headerObservers.set(header, observer);
    header.classList.add('has-mobile-menu');
    setMobileMenu(header, false);
    measure();
    const logo = header.querySelector('.header-logo img');
    function fallbackLogo() {
      if (!logo || !header.dataset.fallbackLogo || logo.dataset.fallbackUsed) return;
      logo.dataset.fallbackUsed = 'true';
      logo.removeAttribute('srcset');
      logo.src = header.dataset.fallbackLogo;
    }
    logo?.addEventListener('error', fallbackLogo);
    if (logo?.complete && !logo.naturalWidth) fallbackLogo();
  }
  function showModal(dialog, trigger) {
    if (!dialog || dialog.open) return;
    const menuHeader = document.querySelector('[data-header].mobile-menu-open');
    if (menuHeader) {
      if (trigger && menuHeader.querySelector('.header-navigation')?.contains(trigger)) trigger = menuHeader.querySelector('[data-mobile-menu-toggle]');
      setMobileMenu(menuHeader, false);
    }
    if (trigger) openers.set(dialog, trigger);
    dialog.showModal();
    dialog.querySelector('input:not([type=hidden]),select,[data-modal-close]')?.focus();
  }
  function closeModal(dialog) { if (dialog?.open) dialog.close(); }
  async function fetchJSON(url, options = {}) {
    const response = await fetch(url, {credentials: 'same-origin', ...options});
    const data = await response.json();
    if (!response.ok) throw new Error(typeof data.description === 'string' ? data.description : (typeof data.message === 'string' ? data.message : strings.cartError));
    return data;
  }
  function replaceCart(markup) {
    const parsed = new DOMParser().parseFromString(markup, 'text/html');
    const content = parsed.querySelector('[data-cart-content]');
    const drawer = document.querySelector('[data-cart-drawer]');
    if (!content || !drawer) throw new Error(strings.cartError || 'Could not load your bag.');
    drawer.querySelector('[data-cart-content]').replaceWith(content);
    document.querySelectorAll('[data-cart-count]').forEach(el => el.textContent = content.dataset.count);
    return drawer;
  }
  async function refreshCart() {
    const drawer = document.querySelector('[data-cart-drawer]');
    if (!drawer) { location.href = path('cart'); return; }
    const url = new URL(path('cart'), location.origin);
    url.searchParams.set('section_id', drawer.dataset.sectionId);
    const response = await fetch(url, {credentials:'same-origin'});
    if (!response.ok) throw new Error(strings.cartError);
    return replaceCart(await response.text());
  }
  async function openBag(trigger) {
    if (config.cartType === 'page') { location.href = path('cart'); return; }
    const drawer = document.querySelector('[data-cart-drawer]');
    showModal(drawer, trigger);
    try { await cartQueue(refreshCart); }
    catch (error) { const el = drawer?.querySelector('[data-cart-error]'); if (el) el.textContent = error.message; else setStatus(error.message); }
  }
  function initInfinite(marker) {
    if (infiniteStates.has(marker)) return;
    const container = marker.parentElement;
    const grid = container.querySelector('[data-product-grid]');
    if (!grid) return;
    const state = {busy:false,observer:null};
    infiniteStates.set(marker, state);
    const status = marker.querySelector('[data-scroll-status]');
    async function load(event) {
      event?.preventDefault();
      if (state.busy || !marker.dataset.next || !marker.isConnected) return;
      state.busy = true;
      marker.dataset.busy = 'true';
      status.textContent = strings.loading || 'Loading products…';
      const nextUrl = marker.dataset.next;
      try {
        const url = new URL(nextUrl, location.href);
        url.searchParams.set('section_id', marker.dataset.sectionId);
        const response = await fetch(url, {credentials:'same-origin'});
        if (!response.ok) throw new Error('Product request failed');
        const doc = new DOMParser().parseFromString(await response.text(),'text/html');
        const nextMarker = doc.querySelector('[data-infinite]');
        const nextGrid = doc.querySelector('[data-product-grid]');
        if (!nextGrid || !nextMarker) throw new Error('Missing product batch');
        if (!marker.isConnected) return;
        [...nextGrid.children].forEach(item => grid.appendChild(item));
        marker.dataset.next = nextMarker.dataset.next || '';
        const link = marker.querySelector('[data-load-more]');
        if (marker.dataset.next && marker.dataset.next !== nextUrl) {
          if (link) link.href = marker.dataset.next;
          status.textContent = '';
          state.observer?.unobserve(marker);
          state.observer?.observe(marker);
        } else {
          marker.dataset.next = '';
          link?.remove();
          status.textContent = strings.end || 'You have viewed all products.';
          state.observer?.disconnect();
        }
        document.dispatchEvent(new CustomEvent('waf:products-loaded',{detail:{container:grid}}));
      } catch (error) {
        status.textContent = strings.retry || 'Could not load products. Select Load more products to retry.';
        state.observer?.disconnect();
      } finally { state.busy = false; marker.removeAttribute('data-busy'); }
    }
    marker.querySelector('[data-load-more]')?.addEventListener('click',load);
    if ('IntersectionObserver' in window && marker.dataset.mode === 'auto' && marker.dataset.next) {
      state.observer = new IntersectionObserver(entries => { if (entries.some(entry => entry.isIntersecting)) load(); },{rootMargin:'0px 0px 500px 0px'});
      state.observer.observe(marker);
    }
  }
  function initProduct(section) {
    if (initialized.has(section)) return;
    initialized.add(section);
    const data = section.querySelector('[data-product-variants]');
    let variants = [];
    try { const parsed = JSON.parse(data?.textContent || '[]'); if (Array.isArray(parsed)) variants = parsed; } catch (_) {}
    const form = section.querySelector('[data-product-form]');
    const button = section.querySelector('[data-add-button]');
    const mediaItems = [...section.querySelectorAll('[data-product-media]')];
    const gallery = section.querySelector('.product-gallery');
    const mobileSwipe = () => section.dataset.galleryStyle === 'grid' && section.dataset.mobileGallery === 'swipe' && matchMedia('(max-width:749px)').matches;
    let activeMedia = Math.max(0, mediaItems.findIndex(media => media.classList.contains('is-active')));
    function selectMedia(id, scroll = false) {
      const index = mediaItems.findIndex(media => String(media.dataset.mediaId) === String(id));
      if (index < 0) return;
      activeMedia = index;
      mediaItems.forEach((media, i) => {
        media.classList.toggle('is-active', i === index);
        if (section.dataset.galleryStyle === 'spotlight') {
          media.hidden = i !== index;
          if (i !== index) media.querySelectorAll('video').forEach(video => video.pause());
        }
      });
      section.querySelectorAll('[data-media-target]').forEach(thumb => thumb.setAttribute('aria-pressed', String(thumb.dataset.mediaTarget === String(id))));
      const count = section.querySelector('[data-gallery-count]');
      if (count) count.textContent = `${index + 1} / ${mediaItems.length}`;
      if (scroll && section.dataset.galleryStyle === 'grid') mediaItems[index].scrollIntoView({block:'nearest',inline:'start',behavior:matchMedia('(prefers-reduced-motion:reduce)').matches?'auto':'smooth'});
    }
    section.addEventListener('click', event => {
      const thumb = event.target.closest('[data-media-target]');
      if (thumb) selectMedia(thumb.dataset.mediaTarget, true);
      if (event.target.closest('[data-gallery-previous]') && mediaItems.length) selectMedia(mediaItems[(activeMedia - 1 + mediaItems.length) % mediaItems.length].dataset.mediaId, true);
      if (event.target.closest('[data-gallery-next]') && mediaItems.length) selectMedia(mediaItems[(activeMedia + 1) % mediaItems.length].dataset.mediaId, true);
    });
    if (mediaItems.length) selectMedia(mediaItems[activeMedia].dataset.mediaId);
    if (mobileSwipe() && mediaItems[activeMedia]) mediaItems[activeMedia].scrollIntoView({block:'nearest',inline:'start',behavior:'auto'});
    let galleryFrame = 0;
    gallery?.addEventListener('scroll', () => {
      if (!mobileSwipe() || galleryFrame) return;
      galleryFrame = requestAnimationFrame(() => {
        galleryFrame = 0;
        const center = gallery.getBoundingClientRect().left + gallery.clientWidth / 2;
        let nearest = activeMedia, distance = Infinity;
        mediaItems.forEach((media, index) => {
          const box = media.getBoundingClientRect();
          const offset = Math.abs(box.left + box.width / 2 - center);
          if (offset < distance) { distance = offset; nearest = index; }
        });
        if (nearest !== activeMedia) selectMedia(mediaItems[nearest].dataset.mediaId);
      });
    }, {passive:true});
    const fallbackSelect = form?.querySelector('[data-variant-select]');
    const optionControls = [...section.querySelectorAll('[data-option-index]')];
    const selectedVariant = variants.find(variant => String(variant.id) === section.dataset.selectedId) || variants.find(variant => variant.available) || variants[0];
    if (!selectedVariant) return;
    optionControls.forEach(input => {
      const value = selectedVariant.options[Number(input.dataset.optionIndex)];
      if (input.tagName === 'SELECT') input.value = value;
      else input.checked = input.value === value;
    });
    const useOptionControls = optionControls.length > 0;
    if (fallbackSelect) {
      fallbackSelect.value = selectedVariant.id;
      fallbackSelect.disabled = useOptionControls;
      const fallback = section.querySelector('[data-variant-fallback]');
      if (fallback) fallback.hidden = useOptionControls || variants.length === 1;
    }
    section.querySelectorAll('[data-product-options]').forEach(el => el.hidden = false);
    function update(changeURL) {
      const options = [];
      section.querySelectorAll('select[data-option-index],input[data-option-index]:checked').forEach(input => options[Number(input.dataset.optionIndex)] = input.value);
      const variant = options.length ? variants.find(v => v.options.every((option,i)=>option===options[i])) : variants.find(v => String(v.id) === (fallbackSelect?.value || section.dataset.selectedId));
      section.querySelectorAll('.option-buttons input[data-option-index]').forEach(input => {
        const index = Number(input.dataset.optionIndex);
        const available = variants.some(candidate => candidate.available && candidate.options.every((value, i) => value === (i === index ? input.value : options[i])));
        input.closest('label')?.classList.toggle('is-sold-out', !available);
      });
      const id = form?.querySelector('.js-variant-id');
      if (id) { id.value = variant?.id || ''; id.disabled = !variant || (fallbackSelect && !fallbackSelect.disabled); }
      if (button) { button.disabled = !variant?.available; button.textContent = !variant ? strings.unavailable : (!variant.available ? strings.soldOut : (button.dataset.addLabel || strings.add)); }
      section.querySelectorAll('[data-product-price]').forEach(el => { el.textContent = variant?.price || ''; });
      section.querySelectorAll('[data-product-compare]').forEach(el => { el.textContent = variant?.compare || ''; el.hidden = !variant?.onSale; });
      const dynamic = section.querySelector('[data-dynamic-checkout]');
      if (dynamic) dynamic.hidden = !variant?.available;
      const quantity = section.querySelector('[data-product-quantity]');
      if (quantity && variant) {
        quantity.min = variant.min; quantity.step = variant.step;
        if (variant.max) quantity.max = variant.max; else quantity.removeAttribute('max');
        if (Number(quantity.value) < variant.min || (variant.max && Number(quantity.value) > variant.max)) quantity.value = variant.min;
      }
      if (changeURL && variant) {
        const url = new URL(location.href); url.searchParams.set('variant', variant.id); history.replaceState({},'',url);
        if (variant.media) {
          selectMedia(variant.media, true);
        }
      }
    }
    section.addEventListener('change',event => { if (event.target.matches('[data-option-index],[data-variant-select]')) update(true); });
    update(false);
    if (form && config.cartType !== 'page') form.addEventListener('submit',async event => {
      event.preventDefault();
      if (form.dataset.busy || button?.disabled) return;
      const errorEl = section.querySelector('[data-product-error]'); if (errorEl) errorEl.textContent = '';
      const body = new FormData(form);
      form.dataset.busy = 'true'; if (button) { button.disabled = true; button.textContent = strings.adding; }
      try {
        await cartQueue(async () => {
          const drawer = document.querySelector('[data-cart-drawer]');
          if (drawer) { body.set('sections',drawer.dataset.sectionId); body.set('sections_url',path('cart')); }
          const added = await fetchJSON(path('cart/add.js'),{method:'POST',body,headers:{Accept:'application/json'}});
          if (drawer && added.sections?.[drawer.dataset.sectionId]) replaceCart(added.sections[drawer.dataset.sectionId]); else await refreshCart();
          showModal(document.querySelector('[data-cart-drawer]'),button);
        });
      } catch (error) { if (errorEl) errorEl.textContent = error.message; }
      finally { delete form.dataset.busy; update(false); }
    });
  }
  function initCountries(select) {
    if (initialized.has(select)) return;
    initialized.add(select);
    if (select.dataset.default) select.value = select.dataset.default;
    const province = document.getElementById(select.dataset.provinceId);
    function update() {
      let options = [];
      try { options = JSON.parse(select.selectedOptions[0]?.dataset.provinces || '[]'); } catch (_) {}
      if (!province) return;
      province.replaceChildren(...options.map(([value,label])=>new Option(label,value)));
      if (province.dataset.default) province.value = province.dataset.default;
      province.hidden = !options.length;
      const label = document.querySelector(`label[for="${province.id}"]`); if (label) label.hidden = !options.length;
    }
    select.addEventListener('change',update); update();
  }
  function init(scope = document) {
    scope.querySelectorAll('[data-header]').forEach(initHeader);
    scope.querySelectorAll('[data-infinite]').forEach(initInfinite);
    scope.querySelectorAll('[data-product]').forEach(initProduct);
    scope.querySelectorAll('[data-address-country]').forEach(initCountries);
    scope.querySelectorAll('dialog').forEach(dialog => {
      if (initialized.has(dialog)) return;
      initialized.add(dialog);
      dialog.addEventListener('click',event => { if (event.target === dialog) { const box=dialog.getBoundingClientRect(); if (event.clientX<box.left || event.clientX>box.right || event.clientY<box.top || event.clientY>box.bottom) closeModal(dialog); } });
      dialog.addEventListener('close',()=>openers.get(dialog)?.focus({preventScroll:true}));
      if (dialog.querySelector('[data-open-modal-on-load]')) showModal(dialog);
    });
    scope.querySelectorAll('[data-announcement]').forEach(announcement => {
      try { if (sessionStorage.getItem(`waf-announcement-${announcement.dataset.announcement}`)) announcement.hidden=true; } catch (_) {}
    });
  }
  document.addEventListener('click',event => {
    const menuToggle = event.target.closest('[data-mobile-menu-toggle]');
    if (menuToggle) { const header = menuToggle.closest('[data-header]'); setMobileMenu(header, menuToggle.getAttribute('aria-expanded') !== 'true'); return; }
    const menuHeader = document.querySelector('[data-header].mobile-menu-open');
    if (menuHeader && !menuHeader.contains(event.target)) setMobileMenu(menuHeader, false);
    const modalTrigger = event.target.closest('[data-modal-open]');
    if (modalTrigger) { event.preventDefault(); showModal(document.getElementById(modalTrigger.dataset.modalOpen),modalTrigger); return; }
    const close = event.target.closest('[data-modal-close]'); if (close) { closeModal(close.closest('dialog')); return; }
    const bag = event.target.closest('[data-bag-open]'); if (bag) { event.preventDefault(); openBag(bag); return; }
    const hide = event.target.closest('[data-hide-announcement]'); if (hide) { const el=hide.closest('[data-announcement]'); el.hidden=true; try { sessionStorage.setItem(`waf-announcement-${el.dataset.announcement}`,'1'); } catch (_) {} }
    const remove = event.target.closest('[data-cart-drawer] [data-cart-remove]');
    if (remove) { event.preventDefault(); changeCart(remove.dataset.key,0); }
  });
  async function changeCart(key, quantity) {
    if (!Number.isInteger(quantity) || quantity<0) return;
    const drawer=document.querySelector('[data-cart-drawer]');
    drawer?.querySelectorAll('input,button').forEach(el=>el.disabled=true);
    try { await cartQueue(async()=>{
      const updated=await fetchJSON(path('cart/change.js'),{method:'POST',headers:{'Content-Type':'application/json',Accept:'application/json'},body:JSON.stringify({id:key,quantity,sections:[drawer.dataset.sectionId],sections_url:path('cart')})});
      if (updated.sections?.[drawer.dataset.sectionId]) replaceCart(updated.sections[drawer.dataset.sectionId]); else await refreshCart();
    }); } catch (error) { const el=drawer?.querySelector('[data-cart-error]'); if(el)el.textContent=error.message; else setStatus(error.message); }
    finally { drawer?.querySelectorAll('input,button').forEach(el=>el.disabled=false); }
  }
  document.addEventListener('change',event=>{
    const select=event.target.closest('[data-sort]');
    if (select) { const url=new URL(location.href);const target=select.closest('[data-sort-form]')?.getAttribute('action');if(target)url.pathname=new URL(target,location.href).pathname;url.searchParams.set('sort_by',select.value);url.searchParams.delete('page');location.href=url.toString(); }
    const quantity=event.target.closest('[data-cart-drawer] [data-cart-quantity]');
    if(quantity && quantity.checkValidity()) changeCart(quantity.dataset.key,Number(quantity.value));
  });
  document.addEventListener('shopify:section:load',event=>init(event.target));
  document.addEventListener('keydown',event=>{
    if (event.key !== 'Escape' || document.querySelector('dialog[open]')) return;
    const header = document.querySelector('[data-header].mobile-menu-open');
    if (header) { event.preventDefault(); setMobileMenu(header, false, true); }
  });
  mobileMedia.addEventListener('change',()=>document.querySelectorAll('[data-header]').forEach(header=>setMobileMenu(header,false)));
  document.addEventListener('shopify:block:select',event=>{
    const header = event.target.closest('[data-header]');
    if (header && mobileMedia.matches) setMobileMenu(header,true);
  });
  document.addEventListener('shopify:section:unload',event=>{
    for(const [marker,state]of infiniteStates){if(event.target.contains(marker)){state.observer?.disconnect();infiniteStates.delete(marker);}}
    for(const [header,observer]of headerObservers){if(event.target.contains(header)){observer?.disconnect();headerObservers.delete(header);}}
  });
  window.addEventListener('pageshow',()=>{document.querySelectorAll('[data-product-form]').forEach(form=>delete form.dataset.busy);});
  init();
})();
